import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import demoData from './demoData'
import { buildKybParams } from './builders'
import { getClientKeyWithAccessToken } from '../lib/security'
import { useSelector } from 'react-redux'

const baseUrl = process.env.REACT_APP_BASEURL

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: async (headers, { getState }) => {
    const { accessToken } = getState().login
    const clientKey = await getClientKeyWithAccessToken(accessToken)
    headers.set('Authorization', `Bearer ${accessToken}`)
    headers.set('x-gueno-type-product', 'DASHBOARD')
    headers.set('client-key', clientKey)
    return headers
  },
  fetchFn: async (...args) => {
    if (process.env.REACT_APP_IS_DEMO) {
      const url = new URL(args[0]?.url)
      const body = demoData[args[0]?.method][url?.pathname] || {}
      return new Response(JSON.stringify(body), {
        status: 200,
        statusText: 'OK',
        headers: {
          'content-type': 'application/json; charset=utf-8',
        },
      })
    }
    return fetch(...args)
  },
})

export const useGuenoFetch =  () => {

  const {accessToken } = useSelector((state) => state.login)


  const guenoFetch = async (url, options) => fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
      'client-key': await getClientKeyWithAccessToken(accessToken),
    },
  });

  return {
    guenoFetch
  }
}

export const guenoApi = createApi({
  reducerPath: 'guenoApi',
  baseQuery,
  keepUnusedDataFor: 5,
  tagTypes: [
    'subjectsByKyb',
    'kybPerson',
    'kybBusiness',
    'kybBatches',
    'kycPersonList',
    'kycPerson',
    'kycPersonChecks',
    'kycCheck',
    'kytImportItems',
    'kyt-consumerUsers',
    'kyt-businessUsers',
    'riskMatrixById',
    'case',
    'kybTimeline',
    'kybSubjectTimeline',
    'casesStatistics',
    'alertsStatistics',
    'lastRiskHistory',
    'userMaster',
  ],
  endpoints: (builder) => ({
    findKybs: builder.query({
      query: ({ limit, offset, page, sortField, sortDirection, countrySegment, columns, ...query }) => {
        const searchParams = buildKybParams(query)

        return {
          url: `/kyb${countrySegment}`,
          params: {
            limit,
            offset,
            page,
            sortField,
            sortDirection,
            columns,
            ...searchParams,
          },
        }
      },
    }),
    createKyb: builder.mutation({
      query: ({ countrySegment, isKyb, body }) => {
        let url = `/kyb${countrySegment}`

        if (!isKyb) {
          url += 'subject'
        }

        return {
          url,
          method: 'POST',
          body,
        }
      },
    }),
    createManualKyb: builder.mutation({
      query: ({ countrySegment, body }) => {
        return {
          url: `/kyb${countrySegment}manual`,
          method: 'POST',
          body,
        }
      },
    }),
    updateKyb: builder.mutation({
      query: ({ id, countrySegment, body }) => {
        const url = `/kyb${countrySegment}${id}`
        return {
          url,
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: ['kybBusiness', 'kybTimeline'],
    }),
    updateKybPerson: builder.mutation({
      query: ({ id, countrySegment, body }) => {
        const url = `/kyb${countrySegment}subject/${id}`
        return {
          url,
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: ['kybPerson', 'kybSubjectTimeline'],
    }),
    findKycs: builder.query({
      query: ({ limit, offset, page, sortField, sortDirection, countrySegment, columns, ...query }) => {
        const searchParams = buildKybParams(query)

        return {
          url: `/kyb${countrySegment}all/subjects`,
          params: {
            limit,
            offset,
            page,
            sortField,
            sortDirection,
            columns,
            ...searchParams,
          },
        }
      },
    }),
    findChecksByKybAndType: builder.query({
      query: ({ kybId, type, countrySegment }) => ({
        url: `/kyb${countrySegment}${kybId}/checks`,
        params: {
          type,
        },
      }),
    }),
    findUserMaster: builder.query({
      query: ({ emailClient }) => {
        return {
          url: `/usermaster/${emailClient}`,
        }
      },
      providesTags: ['userMaster'],
    }),
    updateUserMaster: builder.mutation({
      query: ({ emailClient, body }) => {
        return {
          url: `/usermaster/${emailClient}`,
          method: 'PATCH',
          body,
        }
      },
    }),
    updateKybSubject: builder.mutation({
      query: ({ subjectId, body }) => {
        let url = `/kyb/subject/${subjectId}`

        return {
          url,
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: ['subjectsByKyb'],
    }),
    deleteSubjectKyb: builder.mutation({
      query: ({ kybId, subjectId, relationType, countrySegment }) => {
        let url = `/kyb${countrySegment}${kybId}/subject/${subjectId}?relationType=${relationType}`

        return {
          url,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['subjectsByKyb'],
    }),
    findSubjectsByKyb: builder.query({
      query: ({ id, selectedTab, entityType, page, limit, sortField, sortDirection, countrySegment }) => {
        let url = `/kyb${countrySegment}${id}/${entityType}/${selectedTab}`
        return {
          url,
          params: {
            page,
            limit,
            sortField,
            sortDirection,
          },
        }
      },
      providesTags: ['subjectsByKyb'],
    }),
    getKybsReport: builder.mutation({
      query: ({ countrySegment, fromDate, endDate }) => ({
        url: `/kyb${countrySegment}report`,
        method: 'GET',
        params: {
          fromDate,
          endDate,
        },
        responseHandler: (response) => response.text(),
      }),
    }),
    runKyb: builder.mutation({
      query: ({ documentNumber, subjectId, countrySegment }) => {
        return {
          url: `/kyb${countrySegment}`,
          method: 'POST',
          body: {
            documentNumber,
            subjectId,
          },
        }
      },
    }),
    restartKyb: builder.mutation({
      query: ({ countrySegment, kybId }) => ({
        url: `/kyb${countrySegment}${kybId}/restart`,
        method: 'PATCH',
        body: {},
      }),
      invalidatesTags: ['kybBusiness', 'subjectsByKyb'],
    }),
    restartKybSubject: builder.mutation({
      query: ({ countrySegment, id }) => ({
        url: `/kyb${countrySegment}subject/${id}/restart`,
        method: 'PATCH',
        body: {},
      }),
      invalidatesTags: ['kybPerson', 'subjectsByKyb'],
    }),
    findKybBatches: builder.query({
      query: ({ limit, offset, page, sortField, sortDirection, currentCountry, ...query }) => {
        const searchParams = buildKybParams(query)

        return {
          url: `/kyb/v2/${currentCountry}/batches`,
          params: {
            limit,
            offset,
            page,
            sortField,
            sortDirection,
            ...searchParams,
          },
        }
      },
      providesTags: ['kybBatches'],
    }),
    findKybTimeline: builder.query({
      query: ({ limit, page, country, id }) => {
        return {
          url: `/kyb/v2/${country}/${id}/timeline`,
          params: {
            limit,
            page,
          },
        }
      },
      providesTags: ['kybTimeline'],
    }),
    findKybSubjectTimeline: builder.query({
      query: ({ limit, page, country, id }) => {
        return {
          url: `/kyb/v2/${country}/subject/${id}/timeline`,
          params: {
            limit,
            page,
          },
        }
      },
      providesTags: ['kybSubjectTimeline'],
    }),
    createKybBatch: builder.mutation({
      query: ({ file, currentCountry }) => {
        const formData = new FormData()
        formData.append('file', file)

        return {
          url: `/kyb/v2/${currentCountry}/batches`,
          method: 'POST',
          body: formData,
          formData: true,
        }
      },
      invalidatesTags: ['kybBatches'],
    }),
    findBatches: builder.query({
      query: ({ emailClient, limit, offset, page, type, status }) => {
        let params = {
          emailClient,
          limit,
          offset,
          page,
        }

        if (type) {
          params.type = type
        }

        if (status) {
          params.status = status
        }

        return {
          url: `/batch/userBatches`,
          params,
        }
      },
    }),
    createBatch: builder.mutation({
      query: ({ batchType, file, skipErrors }) => {
        const formData = new FormData()
        formData.append('file', file)

        if (batchType === 'TRANSACTION' && skipErrors) {
          formData.append('skipErrors', true)
        }

        return {
          url: `/batch/kyt/${batchType.toLocaleLowerCase()}`,
          method: 'POST',
          body: formData,
          formData: true,
        }
      },
    }),
    deleteBatch: builder.mutation({
      query: ({ id }) => ({
        url: `/batch/deepDelete/${id}`,
        method: 'DELETE',
        body: {},
      }),
    }),
    getBatchById: builder.query({
      query: ({ id }) => ({
        url: `/batch/getBatch/${id}`,
      }),
    }),
    findKytUserActions: builder.query({
      query: ({ id, page, limit }) => {
        let url = `/kyt/userActions/${id}`
        return {
          url,
          params: {
            page,
            limit,
          },
        }
      },
    }),
    getKybNotifications: builder.query({
      query: ({ countrySegment, ...params }) => ({
        url: `/kyb${countrySegment}all/notifications`,
        method: 'GET',
        params,
      }),
    }),
    getKybNotificationsV2: builder.query({
      query: (params) => ({
        url: `/kyb/notificationsv2`,
        method: 'GET',
        params,
      }),
    }),
    findAvailableUsers: builder.query({
      query: () => ({
        url: `/user/toAssign`,
      }),
    }),
    findKybPersonById: builder.query({
      query: ({ id, countrySegment, languageState }) => ({
        url: `/kyb${countrySegment}subject/${id}`,
        params: {
          ...(languageState === 'pt-BR' && {
            lang: 'PT',
          }),
        },
      }),
      providesTags: ['kybPerson'],
    }),
    findKybBusinessById: builder.query({
      query: ({ id, countrySegment, languageState }) => ({
        url: `/kyb${countrySegment}${id}`,
        params: {
          ...(languageState === 'pt-BR' && {
            lang: 'PT',
          }),
        },
      }),
      providesTags: ['kybBusiness'],
    }),
    checkKyb: builder.mutation({
      query: ({ countrySegment, body }) => ({
        url: `/kyb${countrySegment}check`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['kybPerson', 'kybBusiness', 'subjectsByKyb', 'kybTimeline', 'kybSubjectTimeline'],
    }),
    findReportByKyb: builder.query({
      query: ({ id, currentCountry, entity, documentNumber }) => ({
        url: `/kyb/v2/${currentCountry}/${id}/report`,
        params: {
          entity,
          documentNumber,
        },
      }),
    }),
    createNewComment: builder.mutation({
      query: ({ countrySegment, id, body }) => ({
        url: `/kyb${countrySegment}subject/${id}/comments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['kybPerson', 'kybSubjectTimeline'],
    }),
    createNewCommentBusiness: builder.mutation({
      query: ({ countrySegment, id, body }) => ({
        url: `/kyb${countrySegment}${id}/comments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['kybBusiness', 'kybTimeline'],
    }),
    updateCheck: builder.mutation({
      query: ({ countrySegment, checkId, body }) => ({
        url: `/kyb${countrySegment}check/${checkId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['kybPerson', 'kybBusiness', 'kybTimeline', 'kybSubjectTimeline'],
    }),
    deleteFilePerson: builder.mutation({
      query: ({ countrySegment, id, key }) => {
        let url = `/kyb${countrySegment}subject/${id}/file/${key}`
        return {
          url,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['kybPerson', 'kybSubjectTimeline'],
    }),
    deleteFileBusiness: builder.mutation({
      query: ({ countrySegment, id, key }) => {
        let url = `/kyb${countrySegment}${id}/file/${key}`
        return {
          url,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['kybBusiness', 'kybTimeline'],
    }),
    uploadFilePerson: builder.mutation({
      query: ({ countrySegment, id, body }) => {
        let url = `/kyb${countrySegment}subject/${id}/file`
        return {
          url,
          method: 'POST',
          body,
        }
      },
      invalidatesTags: ['kybPerson', 'kybSubjectTimeline'],
    }),
    uploadFileBusiness: builder.mutation({
      query: ({ countrySegment, id, body }) => {
        let url = `/kyb${countrySegment}${id}/file`
        return {
          url,
          method: 'POST',
          body,
        }
      },
      invalidatesTags: ['kybBusiness', 'kybTimeline'],
    }),
    updateIsPrivateKyb: builder.mutation({
      query: ({ countrySegment, id, body }) => {
        let url = `/kyb${countrySegment}${id}/private`
        return {
          url,
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: ['kybBusiness', 'kybTimeline'],
    }),
    updateSubjectIsPrivate: builder.mutation({
      query: ({ country, id, body }) => {
        let url = `/kyb/${country}/${id}/subject/private`
        return {
          url,
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: ['kybPerson', 'kybTimeline'],
    }),
    updateMonitoredKyb: builder.mutation({
      query: ({ countrySegment, id, body }) => {
        let url = `/kyb${countrySegment}${id}/monitored`
        return {
          url,
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: ['kybBusiness', 'kybTimeline'],
    }),
    updateRiskLevel: builder.mutation({
      query: ({ countrySegment, id, lvl }) => {
        let url = `/kyb${countrySegment}${id}/riskLevel/${lvl}`
        return {
          url,
          method: 'PATCH',
        }
      },
      invalidatesTags: ['kybBusiness', 'kybTimeline'],
    }),
    updateSubjectRiskLevel: builder.mutation({
      query: ({ countrySegment, id, lvl }) => {
        let url = `/kyb${countrySegment}subject/${id}/riskLevel/${lvl}`
        return {
          url,
          method: 'PATCH',
        }
      },
      invalidatesTags: ['kybPerson', 'kybSubjectTimeline'],
    }),
    runRiskMatrix: builder.mutation({
      query: ({ countrySegment, id }) => {
        return {
          url: `/kyb${countrySegment}${id}/riskMatrix`,
          method: 'POST',
        }
      },
      invalidatesTags: ['kybBusiness', 'kybTimeline', 'lastRiskHistory'],
    }),
    runPersonRiskMatrix: builder.mutation({
      query: ({ countrySegment, id }) => {
        return {
          url: `/kyb/person${countrySegment}${id}/riskMatrix`,
          method: 'POST',
        }
      },
      invalidatesTags: ['kybPerson', 'kybSubjectTimeline', 'lastRiskHistory'],
    }),
    createMCC: builder.mutation({
      query: ({ file, listId }) => {
        const formData = new FormData()
        formData.append('file', file)

        return {
          url: `/admin/merchants/${listId}/mcc`,
          method: 'POST',
          body: formData,
          formData: true,
        }
      },
    }),
    getMCCsbyListId: builder.mutation({
      query: ({ listId }) => {
        return {
          url: `/admin/merchants/${listId}/mcc`,
          method: 'GET',
        }
      },
    }),
    updateChecklistInstance: builder.mutation({
      query: ({ checkId, body }) => {
        return {
          url: `/admin/checklistInstances/${checkId}`,
          method: 'PATCH',
          body,
        }
      },
    }),
    findKybMonitoring: builder.query({
      query: () => ({
        url: '/kyb/monitoring',
      }),
    }),
    updateKybMonitoring: builder.mutation({
      query: (body) => ({
        url: '/kyb/monitoring',
        method: 'PATCH',
        body,
      }),
    }),
    createKybMonitoring: builder.mutation({
      query: (body) => ({
        url: '/kyb/monitoring',
        method: 'POST',
        body,
      }),
    }),
    updateKybList: builder.mutation({
      query: ({ file, replaceLists }) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('replaceLists', replaceLists)

        return {
          url: '/kyb/list',
          method: 'POST',
          body: formData,
          formData: true,
        }
      },
    }),
    deleteKybListItems: builder.mutation({
      query: ({ ids }) => {
        return {
          url: `/kyb/list/items`,
          method: 'DELETE',
          body: { ids },
        }
      },
    }),
  }),
})

export const {
  useFindKybsQuery,
  useFindKycsQuery,
  useCreateKybMutation,
  useCreateManualKybMutation,
  useUpdateKybMutation,
  useLazyFindChecksByKybAndTypeQuery,
  useFindUserMasterQuery,
  useUpdateUserMasterMutation,
  useUpdateKybSubjectMutation,
  useDeleteSubjectKybMutation,
  useFindSubjectsByKybQuery,
  useGetKybsReportMutation,
  useRunKybMutation,
  useRestartKybMutation,
  useRestartKybSubjectMutation,
  useFindKybBatchesQuery,
  useFindKybTimelineQuery,
  useFindKybSubjectTimelineQuery,
  useCreateKybBatchMutation,
  useFindBatchesQuery,
  useCreateBatchMutation,
  useDeleteBatchMutation,
  useGetBatchByIdQuery,
  useFindKytUserActionsQuery,
  useGetKybNotificationsQuery,
  useGetKybNotificationsV2Query,
  useFindKybPersonByIdQuery,
  useFindAvailableUsersQuery,
  useUpdateKybPersonMutation,
  useCheckKybMutation,
  useCreateNewCommentMutation,
  useCreateNewCommentBusinessMutation,
  useUpdateCheckMutation,
  useDeleteFilePersonMutation,
  useDeleteFileBusinessMutation,
  useUploadFilePersonMutation,
  useUploadFileBusinessMutation,
  useFindKybBusinessByIdQuery,
  useUpdateMonitoredKybMutation,
  useUpdateIsPrivateKybMutation,
  useUpdateSubjectIsPrivateMutation,
  useUpdateRiskLevelMutation,
  useUpdateSubjectRiskLevelMutation,
  useRunRiskMatrixMutation,
  useRunPersonRiskMatrixMutation,
  useCreateMCCMutation,
  useGetMCCsbyListIdMutation,
  useFindReportByKybQuery,
  useFindKybMonitoringQuery,
  useUpdateKybMonitoringMutation,
  useCreateKybMonitoringMutation,
  useUpdateChecklistInstanceMutation,
  useLazyFindSubjectsByKybQuery,
  useUpdateKybListMutation,
  useDeleteKybListItemsMutation,
} = guenoApi

export default guenoApi
